import { FormHelperText, InputAdornment, InputLabel, OutlinedInput, useTheme } from '@mui/material'
import { ForwardedRef, forwardRef, HTMLInputTypeAttribute, useContext } from 'react'
import { tokens } from 'ComponentLibrary/style-dictionary/output'
import { PiazzaInputContext } from 'ComponentLibrary/src/molecules/Form/PiazzaInputContext'
import { TextInput, type TextInputProps } from 'ComponentLibrary/src/piazza/forms/TextInput/TextInput'

type InputProps = {
    label?: string
    className?: string
    hasError?: boolean
    errorMessage?: string
    disabled?: boolean
    required?: boolean
    onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>
    onFocus?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>
    onKeyDown?: React.KeyboardEventHandler<HTMLInputElement | HTMLTextAreaElement>
    inputFieldStyles?: React.CSSProperties
    unit?: string
    hideLabel?: boolean
    maxRows?: string | number
    multiline?: boolean
    isDefaultValue?: boolean
    ariaLabeledBy?: string
    placeholder?: string
    type?: HTMLInputTypeAttribute
    onChange?: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void
    value: string
    name?: string
    nonEditable?: boolean
    size?: 'small' | 'medium'
    textAlign?: 'left' | 'right' | 'center' | 'justify' | 'match-parent'
    resize?: 'none' | 'both' | 'horizontal' | 'vertical' | 'block' | 'inline'
    endAdornment?: React.ReactNode
    shrinkLabel?: boolean
    hidePlaceholderOnFocus?: boolean
    autoFocus?: boolean
}

//TOOD: The nonEditable prop is added because we don't yet have other variants of inputs available.
//Once we do, remove it, and all its conditional stylings

/**
 * @deprecated Use `TextInput` from the Piazza component library for new work.
 */
export const BaseInput = forwardRef<HTMLInputElement, InputProps>(
    (
        {
            type,
            label,
            className,
            onBlur,
            onFocus,
            onKeyDown,
            onChange,
            hasError,
            errorMessage,
            disabled = false,
            required,
            inputFieldStyles,
            unit,
            hideLabel = false,
            multiline,
            maxRows,
            isDefaultValue,
            ariaLabeledBy,
            value,
            name,
            nonEditable = false,
            size = 'medium',
            textAlign,
            resize,
            placeholder,
            endAdornment,
            shrinkLabel,
            hidePlaceholderOnFocus,
            autoFocus,
        }: InputProps,
        ref: ForwardedRef<HTMLInputElement>
    ) => {
        const theme = useTheme()
        const usePiazzaInputs = useContext(PiazzaInputContext)

        if (usePiazzaInputs) {
            return (
                <TextInput
                    ref={ref}
                    name={name}
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    onFocus={onFocus}
                    onKeyDown={onKeyDown}
                    label={hideLabel || ariaLabeledBy ? undefined : label}
                    aria-label={hideLabel && !ariaLabeledBy ? label : undefined}
                    aria-labelledby={ariaLabeledBy}
                    errorMessage={hasError ? (errorMessage ?? '') : undefined}
                    disabled={disabled}
                    required={required}
                    autoFocus={autoFocus}
                    type={type as TextInputProps['type']}
                    multiline={multiline}
                    maxRows={maxRows}
                    resize={resize}
                    placeholder={placeholder}
                    unit={unit}
                    endAdornment={endAdornment}
                    textAlign={textAlign === 'right' ? 'right' : 'left'}
                />
            )
        }

        const SMALL_MULTILINE_STYLES = {
            '&.MuiInputBase-multiline': {
                padding: '4px 2px 4px 2px',
            },
        }

        const SMALL_INPUT_LABEL_STYLES = {
            top: '-14px',
            fontSize: '14px',
            color: theme.palette.customtext.black54,
            '&.MuiInputLabel-shrink': {
                top: '-6px',
                paddingLeft: '4px',
            },
        }

        const SMALL_INPUT_STYLES = {
            padding: '4px 0 4px 10px',
            height: '32px',
        }

        return (
            <>
                {/* Hide input label completely if we use labeled by */}
                {!ariaLabeledBy && (
                    <InputLabel
                        shrink={shrinkLabel}
                        sx={{
                            ...(size === 'small' ? SMALL_INPUT_LABEL_STYLES : {}),
                            '&:not(.MuiInputLabel-shrink)': { maxWidth: unit ? 'calc(100% - 48px)' : undefined },
                        }}
                        htmlFor={name}
                    >
                        {hideLabel ? '' : label}
                    </InputLabel>
                )}
                <OutlinedInput
                    id={name}
                    name={name}
                    value={value}
                    ref={ref}
                    onChange={onChange}
                    className={className}
                    label={hideLabel || ariaLabeledBy ? '' : label}
                    notched={shrinkLabel}
                    inputProps={{
                        className,
                        style: {
                            color: isDefaultValue ? theme.palette.customtext.black54 : undefined,
                            textAlign:
                                textAlign !== undefined ? textAlign : size === 'small' || unit !== undefined ? 'right' : 'match-parent',
                            ...(size === 'small' ? SMALL_INPUT_STYLES : {}),
                            ...inputFieldStyles,
                            resize,
                            width: '100%',
                        },
                        'aria-label': hideLabel && !ariaLabeledBy ? label : undefined,
                        'aria-labelledby': ariaLabeledBy,
                    }}
                    sx={{
                        backgroundColor: disabled ? tokens.ColorSurfaceDisabled : undefined,
                        margin: size === 'small' ? '-8px 0' : undefined,
                        width: '100%',
                        ...(size === 'small' && multiline ? SMALL_MULTILINE_STYLES : {}),
                        SMALL_MULTILINE_STYLES,
                        '& .MuiOutlinedInput-notchedOutline':
                            nonEditable || disabled
                                ? {
                                      borderColor: 'transparent!important',
                                  }
                                : undefined,
                        '& .MuiInputBase-input:focus::placeholder': { color: hidePlaceholderOnFocus ? 'transparent' : undefined },
                    }}
                    readOnly={nonEditable}
                    error={hasError}
                    disabled={disabled}
                    required={required}
                    type={type}
                    autoFocus={autoFocus}
                    onBlur={onBlur}
                    onFocus={onFocus}
                    onKeyDown={onKeyDown}
                    multiline={multiline}
                    minRows={multiline && size !== 'small' ? 2 : undefined}
                    maxRows={multiline ? maxRows : undefined}
                    placeholder={placeholder}
                    endAdornment={
                        <InputAdornment
                            position={'end'}
                            sx={{
                                '& > *': {
                                    color: isDefaultValue ? theme.palette.customtext.black54 : undefined,
                                },
                            }}
                        >
                            {unit}
                            {endAdornment}
                        </InputAdornment>
                    }
                />
                <FormFieldErrorText size={size} errorMessage={errorMessage} />
            </>
        )
    }
)

export function FormFieldErrorText({ errorMessage, size }: { errorMessage?: string; size: string }) {
    return (
        <FormHelperText
            variant="outlined"
            sx={{
                fontSize: '0.5rem',
                position: 'absolute',
                bottom: size === 'small' ? '-19px' : '-14px',
                whiteSpace: 'nowrap',
                right: '-12px',
            }}
            margin="dense"
            error
        >
            {errorMessage || ' '}
        </FormHelperText>
    )
}

export default BaseInput
